import React, { useMemo, Component } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { MapContainer, TileLayer, Marker, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import { MapPin } from 'lucide-react';
import { LOCATION_COORDS } from '../workspace/mapData';
import 'leaflet/dist/leaflet.css';
// --- Types ---
type BlockType = 'text' | 'table';
interface ContentBlock {
  type: BlockType;
  content: string;
}
// --- Parsing Logic ---
function parseMessageBlocks(content: string): ContentBlock[] {
  const blocks: ContentBlock[] = [];
  const lines = content.split('\n');
  let currentBlock: string[] = [];
  let inTable = false;
  // Helper to flush current block
  const flush = (type: BlockType) => {
    if (currentBlock.length > 0) {
      const text = currentBlock.join('\n');
      blocks.push({
        type,
        content: text
      });
      currentBlock = [];
    }
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isTableLine = line.trim().startsWith('|');
    if (isTableLine) {
      if (!inTable) {
        // Switch from text to table
        flush('text');
        inTable = true;
      }
      currentBlock.push(line);
    } else {
      if (inTable) {
        // Switch from table to text
        flush('table');
        inTable = false;
      }
      currentBlock.push(line);
    }
  }
  // Flush remaining
  flush(inTable ? 'table' : 'text');
  return blocks;
}
function parseMarkdownTable(markdown: string) {
  const lines = markdown.trim().split('\n');
  if (lines.length < 2) return null;
  // Header row
  const headers = lines[0].split('|').map(c => c.trim()).filter(c => c);
  // Separator row (skip)
  // Data rows
  const rows = lines.slice(2).map(line => {
    const cells = line.split('|').slice(1, -1) // Remove first/last empty splits from |...|
    .map(c => c.trim());
    return cells;
  });
  return {
    headers,
    rows
  };
}
// --- Markdown rendering for text blocks ---
function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Process bold and italic inline formatting
  const regex = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      // ***bold italic***
      parts.push(<strong key={match.index} className="font-semibold italic text-white" data-id="element-154">
          {match[2]}
        </strong>);
    } else if (match[3]) {
      // **bold**
      parts.push(<strong key={match.index} className="font-semibold text-white" data-id="element-155">
          {match[3]}
        </strong>);
    } else if (match[4]) {
      // *italic*
      parts.push(<em key={match.index} className="italic text-white/90" data-id="element-156">
          {match[4]}
        </em>);
    } else if (match[5]) {
      // `code`
      parts.push(<code key={match.index} className="px-1.5 py-0.5 rounded bg-white/[0.08] text-[#EC4899] text-[12px] font-mono" data-id="element-157">
          {match[5]}
        </code>);
    }
    lastIndex = match.index + match[0].length;
  }
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : [text];
}
function MarkdownText({
  content
}: {
  content: string;
}) {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    // Empty line
    if (!trimmed) {
      i++;
      continue;
    }
    // Headings
    if (trimmed.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-base font-semibold text-white mt-4 mb-2" data-id="element-158">
          {renderInlineMarkdown(trimmed.slice(4))}
        </h3>);
      i++;
      continue;
    }
    if (trimmed.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-lg font-semibold text-white mt-4 mb-2" data-id="element-159">
          {renderInlineMarkdown(trimmed.slice(3))}
        </h2>);
      i++;
      continue;
    }
    if (trimmed.startsWith('# ')) {
      elements.push(<h1 key={i} className="text-xl font-bold text-white mt-4 mb-2" data-id="element-160">
          {renderInlineMarkdown(trimmed.slice(2))}
        </h1>);
      i++;
      continue;
    }
    // Unordered list items (- or *)
    if (/^[-*]\s/.test(trimmed)) {
      const listItems: React.ReactNode[] = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i].trim())) {
        const itemText = lines[i].trim().replace(/^[-*]\s/, '');
        listItems.push(<li key={i} className="flex gap-2 py-0.5" data-id="element-161">
            <span className="text-[#EC4899]/60 mt-1.5 flex-shrink-0" data-id="element-162">•</span>
            <span data-id="element-163">{renderInlineMarkdown(itemText)}</span>
          </li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="space-y-0.5 my-2" data-id="element-164">
          {listItems}
        </ul>);
      continue;
    }
    // Ordered list items (1. 2. 3.)
    if (/^\d+\.\s/.test(trimmed)) {
      const listItems: React.ReactNode[] = [];
      let num = 1;
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        const itemText = lines[i].trim().replace(/^\d+\.\s/, '');
        listItems.push(<li key={i} className="flex gap-2.5 py-0.5" data-id="element-165">
            <span className="text-[#EC4899]/60 font-medium text-xs mt-0.5 w-4 flex-shrink-0 text-right" data-id="element-166">
              {num}.
            </span>
            <span data-id="element-167">{renderInlineMarkdown(itemText)}</span>
          </li>);
        num++;
        i++;
      }
      elements.push(<ol key={`ol-${i}`} className="space-y-0.5 my-2" data-id="element-168">
          {listItems}
        </ol>);
      continue;
    }
    // Horizontal rule
    if (/^---+$/.test(trimmed)) {
      elements.push(<hr key={i} className="border-white/[0.08] my-3" data-id="element-169" />);
      i++;
      continue;
    }
    // Regular paragraph
    elements.push(<p key={i} className="leading-relaxed my-1.5" data-id="element-170">
        {renderInlineMarkdown(trimmed)}
      </p>);
    i++;
  }
  return <div className="space-y-1" data-id="element-171">{elements}</div>;
}
// --- Components ---
const InlineMapCard = ({
  location
}: {
  location: {
    name: string;
    lat: number;
    lng: number;
    zoom: number;
  };
}) => {
  // Custom icon
  const icon = L.divIcon({
    html: `<div style="display:flex;align-items:center;justify-content:center;width:24px;height:24px;background:#EC4899;border-radius:50%;box-shadow:0 0 10px #EC4899;"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg></div>`,
    className: '',
    iconSize: [24, 24],
    iconAnchor: [12, 24]
  });
  return <div className="my-4 rounded-xl overflow-hidden border border-white/[0.08] shadow-xl h-[240px] relative group" style={{
    zIndex: 0,
    isolation: 'isolate'
  }} data-id="element-172">
      <MapContainer center={[location.lat, location.lng]} zoom={location.zoom} className="h-full w-full" style={{
      background: '#0B1120'
    }} zoomControl={false} attributionControl={false} dragging={true} scrollWheelZoom={false} doubleClickZoom={true} data-id="element-173">
        <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" data-id="element-174" />
        <Marker position={[location.lat, location.lng]} icon={icon} data-id="element-175" />
        <ZoomControl position="bottomright" data-id="element-176" />
      </MapContainer>
      <div className="absolute top-3 left-3 z-[10] px-3 py-1.5 rounded-lg bg-slate-900/80 backdrop-blur-md border border-white/10 text-xs font-medium text-white flex items-center gap-2" data-id="element-177">
        <MapPin size={12} className="text-[#EC4899]" data-id="element-178" />
        <span className="capitalize" data-id="element-179">{location.name}</span>
      </div>
    </div>;
};
const InlineChart = ({
  headers,
  rows
}: {
  headers: string[];
  rows: string[][];
}) => {
  const timeColIndex = headers.findIndex(h => /time|date|hour|day|run|period/i.test(h));
  const data = rows.map(row => {
    const item: any = {};
    headers.forEach((h, i) => {
      const val = row[i];
      const num = parseFloat(val?.replace(/[^0-9.-]/g, ''));
      item[h] = isNaN(num) ? val : num;
    });
    return item;
  });
  const dataKeys = headers.filter((h, i) => {
    if (i === timeColIndex) return false;
    const numCount = data.filter(d => typeof d[h] === 'number').length;
    return numCount > data.length * 0.5;
  });
  if (dataKeys.length === 0 || data.length < 2) return null;
  const MODEL_HEX_COLORS = ['#EC4899', '#3b82f6', '#06b6d4', '#f59e0b', '#8b5cf6', '#10b981', '#f97316', '#a855f7'];
  return <div className="w-full" data-id="element-180">
      {/* Legend — matches workspace ChartWidget */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-white/[0.06]" data-id="element-181">
        {dataKeys.map((key, i) => <div key={key} className="flex items-center gap-1.5" data-id="element-182">
            <div className="w-3 h-[3px] rounded-full" style={{
          backgroundColor: MODEL_HEX_COLORS[i % MODEL_HEX_COLORS.length]
        }} data-id="element-183" />
            <span className="text-[11px] font-medium text-white/80" data-id="element-184">{key}</span>
          </div>)}
      </div>
      <div className="h-[180px] p-3 pt-4" data-id="element-185">
        <ResponsiveContainer width="100%" height="100%" data-id="element-186">
          <LineChart data={data} margin={{
          top: 5,
          right: 10,
          left: -10,
          bottom: 0
        }} data-id="element-187">
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" vertical={true} data-id="element-188" />
            <XAxis dataKey={headers[timeColIndex] || headers[0]} stroke="#475569" tick={{
            fontSize: 10,
            fill: '#64748b'
          }} tickLine={false} axisLine={{
            stroke: 'rgba(148,163,184,0.15)'
          }} interval="preserveStartEnd" data-id="element-189" />
            <YAxis stroke="#475569" tick={{
            fontSize: 10,
            fill: '#64748b'
          }} tickLine={false} axisLine={false} domain={['auto', 'auto']} data-id="element-190" />
            <Tooltip content={({
            active,
            payload,
            label
          }) => {
            if (!active || !payload?.length) return null;
            return <div className="rounded-xl px-4 py-3 border border-white/[0.1] shadow-2xl shadow-black/50 min-w-[160px]" style={{
              background: 'rgba(15, 23, 42, 0.92)',
              backdropFilter: 'blur(20px)'
            }} data-id="element-192">
                    <div className="text-[11px] text-slate-400 mb-2 font-medium" data-id="element-193">
                      {label}
                    </div>
                    <div className="space-y-1.5" data-id="element-194">
                      {payload.map((item: any, idx: number) => <div key={idx} className="flex items-center justify-between gap-4" data-id="element-195">
                          <div className="flex items-center gap-2" data-id="element-196">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                      backgroundColor: item.color
                    }} data-id="element-197" />
                            <span className="text-[11px] text-slate-300 font-medium" data-id="element-198">
                              {item.dataKey}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-1" data-id="element-199">
                            <span className="text-[12px] text-white font-semibold tabular-nums" data-id="element-200">
                              {typeof item.value === 'number' ? item.value.toFixed(2) : item.value}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>;
          }} data-id="element-191" />
            {dataKeys.map((key, i) => <Line key={key} type="monotone" dataKey={key} stroke={MODEL_HEX_COLORS[i % MODEL_HEX_COLORS.length]} strokeWidth={2.5} dot={false} activeDot={{
            r: 4,
            fill: MODEL_HEX_COLORS[i % MODEL_HEX_COLORS.length],
            stroke: '#fff',
            strokeWidth: 2
          }} data-id="element-201" />)}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>;
};
const InlineTable = ({
  content,
  hideCharts
}: {
  content: string;
  hideCharts?: boolean;
}) => {
  const parsed = useMemo(() => parseMarkdownTable(content), [content]);
  if (!parsed) return <pre className="text-xs" data-id="element-202">{content}</pre>;
  const {
    headers,
    rows
  } = parsed;
  const MODEL_HEX_COLORS_TABLE = ['#EC4899', '#3b82f6', '#06b6d4', '#f59e0b', '#8b5cf6', '#10b981', '#f97316', '#a855f7'];
  // Compute column min/max/avg for color coding (matching workspace TableWidget)
  const getColStats = (colIdx: number) => {
    const vals = rows.map(r => parseFloat(r[colIdx]?.replace(/[^0-9.-]/g, ''))).filter(v => !isNaN(v));
    if (vals.length === 0) return null;
    return {
      min: Math.min(...vals),
      max: Math.max(...vals),
      avg: vals.reduce((a, b) => a + b, 0) / vals.length
    };
  };
  const getCellColor = (val: number, stats: {
    min: number;
    max: number;
    avg: number;
  }) => {
    const range = stats.max - stats.min;
    if (range < 0.3) return {
      bg: 'transparent',
      text: 'text-slate-300'
    };
    const isMax = val === stats.max;
    const isMin = val === stats.min;
    const deviation = Math.abs(val - stats.avg);
    const isSignificant = deviation > range * 0.15;
    if (isMax && isSignificant) return {
      bg: 'rgba(219, 39, 119, 0.18)',
      text: 'text-pink-400'
    };
    if (isMin && isSignificant) return {
      bg: 'rgba(59, 130, 246, 0.18)',
      text: 'text-blue-400'
    };
    return {
      bg: 'transparent',
      text: 'text-slate-300'
    };
  };
  const glassStyle: React.CSSProperties = {
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  return <div className="my-3 overflow-hidden rounded-xl border border-white/[0.08] bg-[#0B1120] shadow-xl shadow-black/20 jua-inline-table" data-id="element-203">
      {/* Chart visualization above table */}
      {!hideCharts && <InlineChart headers={headers} rows={rows} data-id="element-204" />}

      <div className="overflow-x-auto" data-id="element-205">
        <table className="w-full text-xs border-collapse" data-id="element-206">
          <thead className="bg-[#0F172A]" data-id="element-207">
            <tr data-id="element-208">
              {headers.map((h, i) => <th key={i} className="px-4 py-2.5 font-medium text-slate-400 whitespace-nowrap border-b border-white/[0.06] text-left" data-id="element-209">
                  {h}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-210">
            {rows.map((row, i) => <tr key={i} className="group hover:bg-white/[0.02] transition-colors" data-id="element-211">
                {row.map((cell, j) => {
              const isBull = cell.includes('Bull') || cell.includes('↑');
              const isBear = cell.includes('Bear') || cell.includes('↓');
              const isWarn = cell.includes('⚠');
              // Try numeric color coding
              const numVal = parseFloat(cell?.replace(/[^0-9.-]/g, ''));
              const colStats = getColStats(j);
              let cellBg = 'transparent';
              let cellTextClass = 'text-slate-300';
              if (!isNaN(numVal) && colStats && j > 0) {
                const colors = getCellColor(numVal, colStats);
                cellBg = colors.bg;
                cellTextClass = colors.text;
              }
              if (isBull) cellTextClass = 'text-emerald-400 font-medium';
              if (isBear) cellTextClass = 'text-red-400 font-medium';
              if (isWarn) cellTextClass = 'text-amber-400 font-medium';
              // First column gets model color dot
              const isFirstCol = j === 0;
              return <td key={j} className={`px-4 py-2 border-b border-white/[0.04] whitespace-nowrap ${isFirstCol ? 'font-medium text-slate-200' : ''}`} data-id="element-212">
                      {isFirstCol ? <div className="flex items-center gap-2" data-id="element-213">
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                    backgroundColor: MODEL_HEX_COLORS_TABLE[i % MODEL_HEX_COLORS_TABLE.length]
                  }} data-id="element-214" />
                          <span data-id="element-215">{cell}</span>
                        </div> : <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${cellTextClass}`} style={{
                  backgroundColor: cellBg
                }} data-id="element-216">
                          {cell}
                        </span>}
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Footer bar — matches workspace widget */}
      <div className="px-4 py-2 flex items-center gap-2 text-[10px] text-white/40 border-t border-white/[0.06]" style={glassStyle} data-id="element-217">
        <span className="font-medium text-white/60" data-id="element-218">{rows.length} rows</span>
        <span className="text-white/20" data-id="element-219">·</span>
        <span data-id="element-220">{headers.length} columns</span>
      </div>
    </div>;
};
export function RichMessageContent({
  content,
  hideCharts
}: {
  content: string;
  hideCharts?: boolean;
}) {
  const blocks = useMemo(() => parseMessageBlocks(content), [content]);
  return <div className="space-y-3" data-id="element-221">
      {blocks.map((block, i) => {
      if (block.type === 'table') {
        return <InlineTable key={i} content={block.content} hideCharts={hideCharts} data-id="element-222" />;
      }
      return <div key={i} data-id="element-223">
            <MarkdownText content={block.content} data-id="element-224" />
          </div>;
    })}
    </div>;
}